/**
 * +Wrapper.tsx - Client-Side Provider Wrapper
 *
 * This wraps all pages with the provider stack.
 * Since we have ssr: false, this only runs on the client.
 *
 * Provider Order:
 * 1. QueryClientProvider - React Query for server state
 * 2. ErrorBoundary - Catches React errors
 * 3. ToastProvider - Toast notifications
 * 4. ConditionalAuthProvider - Better Auth context (migrated from Neon Auth)
 * 5. XPProvider - XP and progress tracking (persists across navigation)
 * 6. SettingsProvider - User settings
 * 7. StatsigWrapper - Feature flags & A/B testing
 * 8. ConsentProvider - GDPR consent management
 * 9. DataCacheProvider - Client-side data cache
 * 10. DarkModeProvider - Dark mode state
 * 11. CareerCardProvider - Career card state
 * 12. FocusModeProvider - Focus mode state
 * 13. FeedbackProvider - Feedback system
 * 14. TutorialProvider - Tutorial state
 * 15. AdminSettingsProvider - Admin settings
 * 16. TierProvider - Tier usage, limits, and feature access
 * 17. NotificationProvider - Real-time notifications
 * 18. PageHeaderProvider - Page header context for Layout
 */

import React, { useCallback } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ErrorBoundary } from '@sentry/react'
import { ConvexReactClient, ConvexProviderWithAuth } from 'convex/react'
import { useAuth } from '@workos-inc/authkit-react'

// Import global CSS - Tailwind and other styles
import '../index.css'

// Create Convex client singleton
const convexUrl = import.meta.env.VITE_CONVEX_URL;
if (!convexUrl) {
  console.warn('VITE_CONVEX_URL not set - Convex features will not work');
}
const convex = convexUrl ? new ConvexReactClient(convexUrl) : null;

// Import all providers
import { ToastProvider } from '../context/ToastContext'
import { ConditionalAuthProvider } from '../context/ConditionalAuthProvider'
import { XPProvider } from '../contexts/XPContext'
import { SettingsProvider } from '../context/SettingsContext'
import { StatsigWrapper } from '../components/StatsigWrapper'
import { ConsentProvider } from '../context/ConsentContext'
import { DataCacheProvider } from '../context/DataCacheContext'
import { DarkModeProvider } from '../context/DarkModeContext'
import { CareerCardProvider } from '../context/CareerCardContext'
import { FocusModeProvider } from '../context/FocusModeContext'
import { FeedbackProvider } from '../context/FeedbackContext'
import { TutorialProvider } from '../context/TutorialContext'
import { AdminSettingsProvider } from '../context/AdminSettingsContext'
import { TierProvider } from '../context/TierContext'
// MIGRATION: Using ConvexNotificationProvider instead of legacy NotificationProvider
// The ConvexNotificationProvider uses real-time Convex subscriptions instead of SSE/polling
import { ConvexNotificationProvider } from '../context/ConvexNotificationContext'
import { PageHeaderProvider } from '../context/PageHeaderContext'

// Import global components
import { OfflineHandler } from '../components/OfflineHandler'
import { NetworkStatusBanner } from '../components/NetworkStatusBanner'
import TutorialPanel from '../components/TutorialPanel'
import { ProfileSyncHandler } from '../components/Auth/ProfileSyncHandler'

// Create Query Client as a singleton to persist across navigations
let queryClient: QueryClient;

function getQueryClient() {
  if (!queryClient) {
    queryClient = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 5 * 60 * 1000, // 5 minutes
          gcTime: 30 * 60 * 1000, // 30 minutes
          retry: 3,
          refetchOnWindowFocus: false,
          networkMode: 'offlineFirst',
        },
      },
    });
  }
  return queryClient;
}

/**
 * Error fallback component for top-level errors
 */
function ErrorFallback({ error, resetError }: { error: unknown; componentStack?: string; eventId?: string; resetError: () => void }) {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const isAuthError = errorMessage?.includes('useAuth') ||
                      errorMessage?.includes('NeonAuthProvider') ||
                      errorMessage?.includes('Neon Auth') ||
                      errorMessage?.includes('Better Auth');

  if (isAuthError) {
    console.error('Auth context lost, reloading page to recover', error);
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
      <div className="max-w-md w-full p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold text-red-600 dark:text-red-400 mb-4">
          {isAuthError ? 'Reconnecting...' : 'Something went wrong'}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {isAuthError
            ? 'Your session was interrupted. The page will reload momentarily...'
            : errorMessage}
        </p>
        {!isAuthError && (
          <button
            onClick={resetError}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try again
          </button>
        )}
        {isAuthError && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Custom hook for Convex authentication using WorkOS AuthKit.
 *
 * This hook must be called directly inside ConvexProviderWithAuth.
 * It bridges WorkOS AuthKit's getAccessToken() with Convex's auth system.
 */
function useAuthFromWorkOS() {
  const { getAccessToken, isLoading, user } = useAuth();

  // Log auth state changes in development
  if (import.meta.env.DEV) {
    console.log('🔐 useAuthFromWorkOS:', {
      isLoading,
      hasUser: !!user,
      userId: user?.id,
      email: user?.email,
    });
  }

  // Create stable fetchAccessToken callback
  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken?: boolean }) => {
      try {
        // WorkOS AuthKit's getAccessToken handles token refresh internally
        const token = await getAccessToken();

        if (import.meta.env.DEV && token) {
          // Decode JWT payload for debugging
          try {
            const parts = token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              console.log('🎫 Convex fetchAccessToken:', {
                hasToken: true,
                tokenLength: token.length,
                forceRefresh: forceRefreshToken,
                jwtIssuer: payload.iss,
                jwtSubject: payload.sub,
                jwtAudience: payload.aud,
              });
            }
          } catch {
            // Ignore decode errors in debug logging
          }
        }

        return token ?? null;
      } catch (error) {
        console.error('Failed to get access token for Convex:', error);
        return null;
      }
    },
    [getAccessToken]
  );

  return {
    isLoading,
    isAuthenticated: !!user,
    fetchAccessToken,
  };
}

// Convex provider that uses WorkOS for authentication
function ConvexClientProvider({ children }: { children: React.ReactNode }) {
  if (!convex) {
    // Convex URL not configured - render without Convex
    return <>{children}</>;
  }

  return (
    <ConvexProviderWithAuth client={convex} useAuth={useAuthFromWorkOS}>
      {children}
    </ConvexProviderWithAuth>
  );
}

export default function Wrapper({ children }: { children: React.ReactNode }) {
  return (
    <ErrorBoundary fallback={(errorData) => <ErrorFallback {...errorData} />} showDialog>
      <QueryClientProvider client={getQueryClient()}>
        <ToastProvider>
          <ConditionalAuthProvider>
            {/* ConvexProvider must be inside AuthProvider to access WorkOS tokens */}
            <ConvexClientProvider>
              <XPProvider>
                <SettingsProvider>
                  <StatsigWrapper>
                    <ConsentProvider>
                      <DataCacheProvider>
                        <DarkModeProvider>
                          <CareerCardProvider>
                            <FocusModeProvider>
                              <FeedbackProvider>
                                <TutorialProvider>
                                  <AdminSettingsProvider>
                                    <TierProvider>
                                      <ProfileSyncHandler />
                                      <OfflineHandler />
                                      <NetworkStatusBanner />
                                      <TutorialPanel />
                                      <ConvexNotificationProvider>
                                        <PageHeaderProvider>
                                          <ErrorBoundary>
                                            <ErrorBoundary>
                                              <div className="h-full w-full">
                                                {children}
                                              </div>
                                            </ErrorBoundary>
                                          </ErrorBoundary>
                                        </PageHeaderProvider>
                                      </ConvexNotificationProvider>
                                    </TierProvider>
                                  </AdminSettingsProvider>
                                </TutorialProvider>
                              </FeedbackProvider>
                            </FocusModeProvider>
                          </CareerCardProvider>
                        </DarkModeProvider>
                      </DataCacheProvider>
                    </ConsentProvider>
                  </StatsigWrapper>
                </SettingsProvider>
              </XPProvider>
            </ConvexClientProvider>
          </ConditionalAuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
